/**
 * Shared sample-findings rendering helpers.
 *
 * The analysis upload form, the pipeline registration form, and the run
 * detail page all render a compact "sample findings" table (single Taxonomy
 * column for taxa). The specimen analysis tab renders a richer per-rank
 * layout and builds its own column model — but reuses the formatting +
 * rank-ordering helpers here so payload knowledge stays in one place. When a
 * new result type ships, extend SAMPLE_COLUMNS_BY_KIND + renderSampleRow
 * alongside the catalog entry in modules/_kinds/.
 */

// Canonical taxonomic rank order. Mirrors VALID_RANKS in modules/_kinds/taxon.js.
// The payload stores taxonomy as a JSONB object whose key order is NOT
// biological (Postgres orders jsonb keys by length then bytewise), so any
// surface that renders a lineage must impose this order explicitly.
export const RANK_ORDER = [
  'domain', 'superkingdom', 'phylum', 'class',
  'order', 'family', 'genus', 'species',
];

export const SAMPLE_COLUMNS_BY_KIND = {
  taxon: ['Taxonomy', 'Abundance', 'Confidence'],
  classification: ['Class'],
};

export function getSampleColumns(kind) {
  return SAMPLE_COLUMNS_BY_KIND[kind] || ['Payload'];
}

// Ranks actually present across a set of taxon findings, in biological order.
export function orderedRanks(findings) {
  const present = new Set();
  for (const f of findings || []) {
    const taxonomy = (f && f.payload && f.payload.taxonomy) || {};
    for (const rank of Object.keys(taxonomy)) {
      if (taxonomy[rank]) present.add(rank);
    }
  }
  return RANK_ORDER.filter((r) => present.has(r));
}

export function rankLabel(rank) {
  return rank.charAt(0).toUpperCase() + rank.slice(1);
}

export function formatTaxonomy(taxonomy) {
  if (!taxonomy) return '—';
  const parts = RANK_ORDER
    .filter((rank) => taxonomy[rank])
    .map((rank) => `${taxonomy[rank]} (${rank})`);
  if (parts.length === 0) return '—';
  return parts.join(' › ');
}

export function formatNumericWithKind(obj) {
  if (!obj || obj.value == null) return '—';
  return `${obj.value} ${obj.kind || ''}`.trim();
}

export function renderSampleRow(finding, kind) {
  const payload = (finding && finding.payload) || {};
  if (kind === 'taxon') {
    return [
      formatTaxonomy(payload.taxonomy),
      formatNumericWithKind(payload.abundance),
      formatNumericWithKind(payload.confidence),
    ];
  }
  if (kind === 'classification') {
    return [payload.class_name || '—'];
  }
  // Unknown result type — render the payload defensively so the user still
  // sees something. Replace with a kind-specific renderer when a new kind ships.
  return [JSON.stringify(payload).slice(0, 120)];
}

// Comparator for sortable findings tables. Nulls/blanks always sink (regardless
// of direction); numbers compare numerically; everything else case-insensitive.
export function compareSortValues(a, b, direction) {
  const aNull = a == null || a === '' || a === '—';
  const bNull = b == null || b === '' || b === '—';
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  let cmp;
  if (typeof a === 'number' && typeof b === 'number') {
    cmp = a - b;
  } else {
    cmp = String(a).toLowerCase().localeCompare(String(b).toLowerCase());
  }
  return direction === 'desc' ? -cmp : cmp;
}
